import React from 'react';
import { Link } from 'react-router-dom';

export interface DashboardFeatureCardProps {
  to: string;
  title: string;
  description: string;
  beforeImage?: string;
  afterImage?: string;
  tool?: string;
  variant?: 'default' | 'reels';
  reelsImage?: string;
  reelsVideo?: string;
}

const ImagePlaceholder: React.FC<{ label: string }> = ({ label }) => (
  <div className="w-full h-full bg-gray-100 border border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center text-center px-2">
    <i className="fas fa-image text-gray-300 text-lg mb-1" aria-hidden="true" />
    <span className="text-[10px] text-gray-400 font-medium">{label}</span>
  </div>
);

const OverlappingImages: React.FC<{
  beforeImage?: string;
  afterImage?: string;
  title: string;
}> = ({ beforeImage, afterImage, title }) => (
  <div className="relative h-60 sm:h-64 md:h-72 mb-2.5">
    <div className="absolute right-0 bottom-0 w-[78%] h-[98%] z-10">
      {afterImage ? (
        <img
          src={afterImage}
          alt={`${title} after`}
          className="w-full h-full object-cover rounded-2xl shadow-md group-hover:scale-[1.02] transition-transform duration-500"
        />
      ) : (
        <ImagePlaceholder label="After" />
      )}
    </div>
    <div className="absolute left-0 top-0 w-[36%] h-[58%] z-20">
      {beforeImage ? (
        <img
          src={beforeImage}
          alt={`${title} before`}
          className="w-full h-full object-cover rounded-2xl shadow-sm"
        />
      ) : (
        <ImagePlaceholder label="Before" />
      )}
    </div>
  </div>
);

const ReelsMedia: React.FC<{
  reelsImage?: string;
  reelsVideo?: string;
}> = ({ reelsImage, reelsVideo }) => (
  <div className="relative h-60 sm:h-64 md:h-72 mb-2.5 rounded-2xl overflow-hidden">
    <div className="flex h-full rounded-2xl overflow-hidden">
      <div className="flex-1 h-full">
        {reelsImage ? (
          <img
            src={reelsImage}
            alt="Fashion model"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <ImagePlaceholder label="Preview" />
        )}
      </div>
      <div className="flex-1 h-full">
        {reelsVideo ? (
          <video
            src={reelsVideo}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            autoPlay
            muted
            loop
            playsInline
          />
        ) : (
          <ImagePlaceholder label="Video" />
        )}
      </div>
    </div>
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-none">
      <img
        src="/images/Dashboard-Images/Feature-Images/imagecopy.png"
        alt=""
        className="w-9 h-6"
        aria-hidden="true"
      />
    </div>
  </div>
);

const DashboardFeatureCard: React.FC<DashboardFeatureCardProps> = ({
  to,
  title,
  description,
  beforeImage,
  afterImage,
  tool,
  variant = 'default',
  reelsImage,
  reelsVideo,
}) => (
  <Link
    to={to}
    state={tool ? { tool } : undefined}
    className="group block bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300 w-full sm:w-[calc(50%-6px)] lg:w-[calc(33.333%-11px)] xl:w-[calc(25%-12px)] p-3 sm:p-3.5"
  >
    {variant === 'reels' ? (
      <ReelsMedia reelsImage={reelsImage} reelsVideo={reelsVideo} />
    ) : (
      <OverlappingImages beforeImage={beforeImage} afterImage={afterImage} title={title} />
    )}

    <h2 className="text-base font-bold text-text-heading leading-snug">{title}</h2>
    <p className="text-sm text-text-description line-clamp-2 mt-1 leading-relaxed">{description}</p>
  </Link>
);

export default DashboardFeatureCard;
